import { authService } from '@/api/services/authService';
import { NewYearGoalBanner } from '@/components/goalPlanning/NewYearGoalBanner';
import { AppUpdateBanner } from '@/components/layout/AppUpdateBanner';
import SearchCommand from '@/components/SearchCommand';
import { ContactForm } from '@/components/settings/ContactForm';
import { PageSkeleton } from '@/components/ui/skeletons';
import { DetailPreviewProvider } from '@/contexts/DetailPreviewContext';
import { PageChromeProvider, usePageChrome } from '@/contexts/PageChromeContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAppVersionCheck } from '@/hooks/useAppVersionCheck';
import { useLanguage } from '@/i18n/language-hooks';
import {
  BOTTOM_NAV_HEIGHT,
  BOTTOM_NAV_SCROLL_PADDING,
  mobileMainBottomPaddingClass,
  mobileShellBottomReserveClass,
} from '@/lib/app-layout';
import { setPwaUpdateCallback } from '@/lib/pwaRegister';
import { SIBLING_PILLAR_ROUTES, useRoutePrefetch } from '@/lib/queryPrefetch';
import { cn } from '@/lib/utils';
import { getGoalPlanningContext } from '@/services/goalPlanningContext';
import {
  CandlestickChart,
  LayoutGrid,
  Settings,
  TrendingUp,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { Suspense, useCallback, useEffect, useState, type CSSProperties } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AppHeader } from './AppHeader';
import { DetailAside } from './DetailAside';
import { Sidebar } from './Sidebar';

const SIDEBAR_COLLAPSED_KEY = 'fintecy.sidebar.collapsed';

interface BottomNavItemProps {
  icon: LucideIcon;
  label: string;
  href: string;
  active: boolean;
}

const BottomNavItem = ({ icon: Icon, label, href, active }: BottomNavItemProps) => {
  return (
    <Link
      to={href}
      className={cn(
        'flex flex-1 flex-col items-center justify-center gap-1 py-2 transition-colors',
        active ? 'text-fin-accent' : 'text-fin-gray-400'
      )}
    >
      <Icon size={20} aria-hidden />
      <span className='text-xs font-medium'>{label}</span>
    </Link>
  );
};

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayoutContent = ({ children }: MainLayoutProps) => {
  const location = useLocation();
  const { t } = useLanguage();
  const { scrollRef, surface } = usePageChrome();
  const isAuthenticated: boolean = authService.isAuthenticated();
  const prefetch = useRoutePrefetch();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [showGoalBanner, setShowGoalBanner] = useState(false);
  const isMobile = useIsMobile();
  const { updateAvailable, reload, check } = useAppVersionCheck();

  useEffect(() => {
    setPwaUpdateCallback(() => {
      void check();
    });
  }, [check]);

  useEffect(() => {
    try {
      if (localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1') setCollapsed(true);
    } catch {
      /* ignore */
    }
    setHydrated(true);
  }, []);

  const persistCollapsed = useCallback((next: boolean) => {
    setCollapsed(next);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const context = getGoalPlanningContext();
      setShowGoalBanner(context.shouldShow);
    }
  }, [isAuthenticated]);

  // After the first pillar paints, warm the other pillars' chunks + data while
  // idle so the most likely next destinations land instantly. Self-guards on
  // data-saver / 2G inside prefetchRouteData.
  useEffect(() => {
    if (!isAuthenticated) return;
    const run = () => SIBLING_PILLAR_ROUTES.forEach((route) => prefetch(route));
    const hasIdle = typeof window.requestIdleCallback === 'function';
    const id = hasIdle ? window.requestIdleCallback(run) : window.setTimeout(run, 1500);
    return () => {
      if (hasIdle) window.cancelIdleCallback(id);
      else window.clearTimeout(id);
    };
  }, [isAuthenticated, prefetch]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setIsSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const rail = hydrated && collapsed;

  const navItems = [
    { icon: LayoutGrid, label: t('nav.overview'), href: '/overview' },
    { icon: Wallet, label: t('nav.banking'), href: '/banking/accounts' },
    { icon: CandlestickChart, label: t('nav.trade'), href: '/trade' },
    { icon: TrendingUp, label: t('nav.planning'), href: '/planning' },
    { icon: Settings, label: t('settings.title'), href: '/settings' },
  ];

  const isPathActive = (path: string) => {
    if (path === '/overview') {
      return (
        location.pathname === '/' ||
        location.pathname === '/overview' ||
        location.pathname.startsWith('/overview/')
      );
    }
    if (path === '/banking/accounts') {
      return (
        location.pathname === '/banking' ||
        location.pathname === '/banking/accounts' ||
        location.pathname === '/accounts' ||
        location.pathname.startsWith('/accounts/') ||
        location.pathname.startsWith('/banking/')
      );
    }
    if (path === '/settings') {
      return location.pathname === '/settings' || location.pathname.startsWith('/settings/');
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const layoutStyle = {
    '--bottom-nav-height': BOTTOM_NAV_HEIGHT,
    '--bottom-nav-scroll-padding': BOTTOM_NAV_SCROLL_PADDING,
  } as CSSProperties;

  return (
    <div
      className='bg-fin-sidebar flex h-dvh max-h-dvh min-h-0 overflow-hidden'
      style={layoutStyle}
    >
      <Sidebar
        collapsed={rail}
        onToggleCollapse={() => persistCollapsed(!collapsed)}
        onContactFormOpen={() => setIsContactFormOpen(true)}
      />

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col overflow-hidden',
          mobileShellBottomReserveClass
        )}
      >
        <AppHeader isAuthenticated={isAuthenticated} onSearchOpen={() => setIsSearchOpen(true)} />

        {showGoalBanner && isAuthenticated && (
          <NewYearGoalBanner onDismiss={() => setShowGoalBanner(false)} />
        )}

        <div className='flex min-h-0 flex-1 gap-3 overflow-hidden px-3 pb-3 md:pr-4 md:pb-4 md:pl-0'>
          <main
            ref={(el) => {
              scrollRef.current = el;
            }}
            className={cn(
              'bg-fin-gray-900 min-h-0 flex-1 overflow-y-auto overscroll-y-contain rounded-xl md:pb-6',
              mobileMainBottomPaddingClass,
              surface === 'flush' ? 'px-0 py-0' : 'px-3 py-3 md:px-6 md:py-6'
            )}
          >
            <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
          </main>
          <DetailAside />
        </div>
      </div>

      <nav className='border-fin-gray-800 bg-background fixed inset-x-0 bottom-0 z-50 border-t pb-[env(safe-area-inset-bottom,0px)] md:hidden'>
        <div className='flex h-16 items-center justify-around'>
          {navItems.map((item) => (
            <BottomNavItem
              key={item.href}
              icon={item.icon}
              label={item.label}
              href={item.href}
              active={isPathActive(item.href)}
            />
          ))}
        </div>
      </nav>

      <SearchCommand open={isSearchOpen} onOpenChange={setIsSearchOpen} isMobile={isMobile} />
      <ContactForm isOpen={isContactFormOpen} onOpenChange={setIsContactFormOpen} />
      {isAuthenticated ? <AppUpdateBanner visible={updateAvailable} onReload={reload} /> : null}
    </div>
  );
};

export const MainLayout = ({ children }: MainLayoutProps) => {
  return (
    <PageChromeProvider>
      <DetailPreviewProvider>
        <MainLayoutContent>{children}</MainLayoutContent>
      </DetailPreviewProvider>
    </PageChromeProvider>
  );
};

export default MainLayout;
